import React, { useState, useEffect } from 'react';
import { PageContainer } from '@/components/layout/PageContainer';
import { User, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';
import { useProfile, UpdateProfileData } from '@/hooks/use-profile';
import ProfileForm from '@/components/profile/ProfileForm';
import InsuranceInfo from '@/components/profile/InsuranceInfo';
import EmergencyContact from '@/components/profile/EmergencyContact';
import AlertMessage from '@/components/ui/AlertMessage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/lib/supabase-client';

// Fallback form that works purely from auth user metadata
const FallbackProfileForm: React.FC = () => {
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    birth_date: '',
    gender: 'prefer-not-to-say',
    email: '',
    phone: '',
  });

  // Pre-populate from auth user metadata on mount
  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.firstName || '',
        last_name: user.lastName || '',
        birth_date: user.dateOfBirth || '',
        gender: user.gender || 'prefer-not-to-say',
        email: user.email || '',
        phone: user.phoneNumber || '',
      });
    }
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value: string) => {
    setFormData(prev => ({ ...prev, gender: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveStatus('idle');
    try {
      // Save to Supabase auth user_metadata since the DB table is unavailable
      const { error } = await supabase.auth.updateUser({
        data: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          gender: formData.gender,
          date_of_birth: formData.birth_date,
          phone_number: formData.phone,
        },
      });
      if (error) throw error;
      setSaveStatus('success');
    } catch (err) {
      console.error('Error saving profile metadata:', err);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Complete Your Profile</CardTitle>
        <p className="text-sm text-[var(--text-secondary)] mt-1">
          Your profile information is stored securely. Fill in the details below to get started.
        </p>
      </CardHeader>
      <CardContent>
        {saveStatus === 'success' && (
          <div className="flex items-center gap-2 mb-4 p-3 rounded-md bg-green-50 text-green-700 border border-green-200">
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm">Profile saved successfully.</span>
          </div>
        )}
        {saveStatus === 'error' && (
          <div className="flex items-center gap-2 mb-4 p-3 rounded-md bg-red-50 text-red-700 border border-red-200">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm">Could not save profile. Please try again.</span>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label htmlFor="fb_first_name" className="block text-sm font-medium text-[var(--text-primary)]">
                First Name
              </label>
              <Input
                id="fb_first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                placeholder="First name"
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="fb_last_name" className="block text-sm font-medium text-[var(--text-primary)]">
                Last Name
              </label>
              <Input
                id="fb_last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                placeholder="Last name"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label htmlFor="fb_birth_date" className="block text-sm font-medium text-[var(--text-primary)]">
                Date of Birth
              </label>
              <Input
                id="fb_birth_date"
                name="birth_date"
                type="date"
                value={formData.birth_date}
                onChange={handleChange}
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="fb_gender" className="block text-sm font-medium text-[var(--text-primary)]">
                Gender
              </label>
              <Select value={formData.gender} onValueChange={handleSelectChange}>
                <SelectTrigger aria-label="Gender">
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                  <SelectItem value="prefer-not-to-say">Prefer not to say</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <label htmlFor="fb_email" className="block text-sm font-medium text-[var(--text-primary)]">
              Email Address
            </label>
            <Input
              id="fb_email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your.email@example.com"
              disabled
              aria-describedby="email-note"
            />
            <p id="email-note" className="text-xs text-[var(--text-tertiary)]">
              Email is managed through your account settings.
            </p>
          </div>

          <div className="space-y-1">
            <label htmlFor="fb_phone" className="block text-sm font-medium text-[var(--text-primary)]">
              Phone Number
            </label>
            <Input
              id="fb_phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder="(123) 456-7890"
            />
          </div>

          <Button type="submit" disabled={isSaving} className="flex items-center gap-2">
            <Save className="w-4 h-4" />
            {isSaving ? 'Saving...' : 'Save Profile'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

const Profile: React.FC = () => {
  const { user } = useAuth();
  const {
    profile,
    isLoadingProfile,
    profileError,
    isUpdatingProfile,
    updateProfileError,
    updateProfile,
  } = useProfile();

  const handleUpdateProfile = async (formData: UpdateProfileData): Promise<void> => {
    console.log('Updating profile with:', formData);
    updateProfile(formData);
  };

  // Determine whether the DB-backed profile is available.
  // A "not found" or table-missing error means we fall back to the auth-context user.
  const dbProfileUnavailable =
    !isLoadingProfile &&
    (profileError != null || profile == null);

  return (
    <PageContainer
      title="Your Profile"
      description="View and update your personal information, insurance details, and emergency contacts."
      icon={<User />}
    >
      {isLoadingProfile && (
        <div className="flex justify-center items-center py-10">
          <p className="text-[var(--text-secondary)]">Loading profile...</p>
        </div>
      )}

      {updateProfileError && (
        <AlertMessage
          type="error"
          title="Error Updating Profile"
          message={updateProfileError.message || 'Could not update your profile. Please try again.'}
        />
      )}

      {/* DB profile loaded successfully — show the full form with insurance + emergency contact */}
      {profile && !isLoadingProfile && (
        <div className="space-y-8">
          <ProfileForm
            profileData={profile}
            onSubmit={handleUpdateProfile}
            isSubmitting={isUpdatingProfile}
          />

          <InsuranceInfo
            insuranceProvider={profile.insurance_provider}
            insuranceNumber={profile.insurance_number}
          />

          <EmergencyContact
            name={(profile as Record<string, unknown>)['emergency_contact_name'] as string | null | undefined}
            phone={(profile as Record<string, unknown>)['emergency_contact_phone'] as string | null | undefined}
            relationship={(profile as Record<string, unknown>)['emergency_contact_relationship'] as string | null | undefined}
          />
        </div>
      )}

      {/* DB profile unavailable — show fallback form backed by auth user metadata */}
      {dbProfileUnavailable && user && (
        <div className="space-y-6">
          {profileError && (
            <AlertMessage
              type="info"
              title="Limited Profile Mode"
              message="Your extended profile is not yet set up. You can still update your basic information below."
            />
          )}
          <FallbackProfileForm />
        </div>
      )}

      {/* No user at all — should not normally be reached inside a protected route */}
      {dbProfileUnavailable && !user && !isLoadingProfile && (
        <AlertMessage
          type="error"
          title="Not Signed In"
          message="Please sign in to view your profile."
        />
      )}
    </PageContainer>
  );
};

export default Profile;
